import { type Mapper } from '@coris-ts/repository/mapper/mapper'
import { type MediaPageEntity, type MediaTotalsEntity } from '../../data/entities/media.entity'
import { type VideoData, type VideosResponse, type VideosSummary, type VideosTimeSeriesData } from '../models/videos'

const VIDEO_COLORS = [
  '#3B82F6',
  '#F59E0B',
  '#10B981',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#84CC16',
  '#F97316',
  '#6366F1'
]

export class MediaPageToVideosMapper implements Mapper<MediaPageEntity, { videos: VideoData[]; totalVideos: number }> {
  public map(from: MediaPageEntity): { videos: VideoData[]; totalVideos: number } {
    return {
      videos: from.items.map((item, index) => ({
        id: `video-${index}`,
        name: item.title,
        imageUrl: item.icon_url,
        color: VIDEO_COLORS[index % VIDEO_COLORS.length],
        timesPlayed: item.times_played.current,
        timesPlayedChange: item.times_played.diff,
        uniqueListeners: item.unique_listeners.current,
        uniqueListenersChange: item.unique_listeners.diff
      })),
      totalVideos: from.total
    }
  }
}

export class MediaTotalsToVideosSummaryMapper
  implements Mapper<MediaTotalsEntity, { summary: VideosSummary; timeSeries: VideosTimeSeriesData[] }>
{
  public map(from: MediaTotalsEntity): { summary: VideosSummary; timeSeries: VideosTimeSeriesData[] } {
    return {
      summary: {
        totalTimesViewed: from.times_played.current,
        totalTimesViewedChange: from.times_played.diff,
        uniqueViewers: from.unique_listeners.current,
        uniqueViewersChange: from.unique_listeners.diff
      },
      timeSeries: from.periods.map((period) => ({
        date: period.date,
        timesViewed: period.times_played,
        uniqueViewers: period.unique_listeners
      }))
    }
  }
}

export class CombinedMediaToVideosResponseMapper
  implements Mapper<{ page: MediaPageEntity; totals: MediaTotalsEntity }, VideosResponse>
{
  private readonly pageMapper = new MediaPageToVideosMapper()
  private readonly totalsMapper = new MediaTotalsToVideosSummaryMapper()

  public map(from: { page: MediaPageEntity; totals: MediaTotalsEntity }): VideosResponse {
    const { videos, totalVideos } = this.pageMapper.map(from.page)
    const { summary, timeSeries } = this.totalsMapper.map(from.totals)

    return {
      summary,
      timeSeries,
      videos,
      totalVideos
    }
  }
}
